import React, { useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import combineStyles from 'classnames';
import Typography from '@mui/material/Typography';
import OldThemedButton from '../components/OldThemedButton';
import { selectInvalidQuestions } from '../redux/selectors/survey_selectors';
import { createSurveyUriWithQueryParams } from '../helpers/link_helper';
import { submitSurvey, gotoQuestion, gotoSurveyPage } from '../redux/actions';
import { hasMatchedCondition } from '../helpers/logic_helper';
import { Button } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../redux/ReduxProvider';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  surveyIsSubmitting: {
    opacity: 0.3,
  },
  messageContainer: {
    textAlign: 'center',
    marginBottom: 16,
  },
  message: {
    fontSize: 32,
    color: theme.palette.mainaccent,
  },
  errorMessage: {
    color: theme.palette.unfavorable,
  },
  btn: {
    height: 48,
    marginRight: 8,
    '&:hover': {
      backgroundColor: '#0B4E54',
    },
  },
  backBtn: {
    color: '#0F7B85',
    backgroundColor: theme.palette.common.white,
    height: 48,
    marginRight: 8,
    '&:hover': {
      backgroundColor: '#F3FBFC',
    },
    display: 'inline-flex',
    alignItems: 'center',
    border: 0,
    borderRadius: 4,
  },
}));

const SubmitPage = () => {
  const styles = useStyles();
  const { survey_code: surveyCode = null } = useParams();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    isSubmitting: surveyIsSubmitting,
    isSubmitted: surveyIsSubmitted,
    isExpired: surveyExpired,
    showSubmitPage,
    error,
  } = useAppSelector((state) => state.survey);

  const answers = useAppSelector((state) => state.answers);

  const invalidQuestions = useAppSelector(selectInvalidQuestions);
  const visibleInvalidQuestions = invalidQuestions.filter((q) => {
    if (!q.displayConditions || q.displayConditions.length <= 0) return true;
    const isVisible = hasMatchedCondition(q.displayConditions, answers);
    return isVisible;
  });
  const hasVisibleInvalidQuestions = visibleInvalidQuestions.length > 0;

  const createSurveyUri = createSurveyUriWithQueryParams(location.search);

  useEffect(() => {
    if (!showSubmitPage) navigate(createSurveyUri(surveyCode));
    // eslint-disable-next-line
  }, [showSubmitPage]);

  useEffect(() => {
    if (hasVisibleInvalidQuestions) {
      dispatch(gotoQuestion(visibleInvalidQuestions[0].id));
    }
    if (showSubmitPage && (hasVisibleInvalidQuestions || surveyExpired || surveyIsSubmitted)) {
      dispatch(gotoSurveyPage());
    }
    // eslint-disable-next-line
  }, [hasVisibleInvalidQuestions, showSubmitPage, surveyExpired, surveyIsSubmitted]);

  const onClickReturnToSurvey = (e) => {
    if (e) e.preventDefault();
    dispatch(gotoSurveyPage());
  };

  const onClickSubmit = (e) => {
    if (e) e.preventDefault();
    dispatch(submitSurvey(surveyCode));
  };

  return (
    <div
      className={combineStyles(styles.root, {
        [styles.surveyIsSubmitting]: surveyIsSubmitting,
      })}
    >
      {!error ? (
        <SubmitPartial
          surveyIsSubmitting={surveyIsSubmitting}
          onClickReturnToSurvey={onClickReturnToSurvey}
          onClickSubmit={onClickSubmit}
        />
      ) : (
        <ErrorPartial
          errorMessage={error}
          surveyIsSubmitting={surveyIsSubmitting}
          onClickReturnToSurvey={onClickReturnToSurvey}
          onClickSubmit={onClickSubmit}
        />
      )}
    </div>
  );
};

const SubmitPartial = ({ surveyIsSubmitting, onClickReturnToSurvey, onClickSubmit }) => {
  const styles = useStyles();
  const { t } = useTranslation();

  return (
    <>
      <div className={styles.messageContainer}>
        <Typography className={styles.message}>{t('survey.confirm')}</Typography>
      </div>

      <CallToActions
        surveyIsSubmitting={surveyIsSubmitting}
        onClickReturnToSurvey={onClickReturnToSurvey}
        onClickSubmit={onClickSubmit}
      />
    </>
  );
};

const ErrorPartial = ({ errorMessage, surveyIsSubmitting, onClickReturnToSurvey, onClickSubmit }) => {
  const styles = useStyles();
  const { t } = useTranslation();

  return (
    <>
      <div className={styles.messageContainer}>
        <Typography className={combineStyles(styles.message, styles.errorMessage)}>
          {t('survey.error.title')}
        </Typography>
        <Typography>{errorMessage}</Typography>
      </div>

      <CallToActions
        surveyIsSubmitting={surveyIsSubmitting}
        onClickReturnToSurvey={onClickReturnToSurvey}
        onClickSubmit={onClickSubmit}
        isRetry
      />
    </>
  );
};

const CallToActions = ({ surveyIsSubmitting, onClickReturnToSurvey, onClickSubmit, isRetry = false }) => {
  const styles = useStyles();
  const { t } = useTranslation();

  const questions = useAppSelector((state) => state.questions);

  const submitLabels = {
    tryAgain: t('survey.tryagain'),
    submit: t('survey.submit'),
  };
  const submitLabel = isRetry ? submitLabels.tryAgain : submitLabels.submit;

  return (
    <div>
      {!!questions.length && (
        <Button className={styles.backBtn} disabled={surveyIsSubmitting} onClick={onClickReturnToSurvey}>
          {t('survey.return')}
        </Button>
      )}

      <OldThemedButton className={styles.btn} disabled={surveyIsSubmitting} onClick={onClickSubmit}>
        {submitLabel}
      </OldThemedButton>
    </div>
  );
};

export default SubmitPage;
